import { FC, ReactNode, createContext, useCallback, useContext, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

const STORAGE_KEY = 'navigation_history_stack';

interface NavigationHistoryContextValue {
  hasHistory: boolean;
  goBack: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const NavigationHistoryContext = createContext<NavigationHistoryContextValue>({
  hasHistory: false,
  goBack: noop,
});

export const useNavigationHistory = () => useContext(NavigationHistoryContext);

export const resetNavigationHistory = () => {
  sessionStorage.removeItem(STORAGE_KEY);
};

const getPersistedStack = (): string[] => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const persistStack = (stack: string[]) => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack));
};

export const NavigationHistoryProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const stack = useRef(getPersistedStack());
  const prevLocationKey = useRef(location.key);
  const prevPathname = useRef(location.pathname);
  const pendingBackTarget = useRef<string | null>(null);

  if (location.key !== prevLocationKey.current) {
    const previousPath = prevPathname.current;
    prevLocationKey.current = location.key;
    prevPathname.current = location.pathname;

    const expectedBackTarget = pendingBackTarget.current;
    pendingBackTarget.current = null;

    if (expectedBackTarget && expectedBackTarget === location.pathname) {
      stack.current = stack.current.slice(0, -1);
      persistStack(stack.current);
    } else if (navigationType === 'PUSH' && previousPath !== location.pathname) {
      stack.current = [...stack.current, previousPath];
      persistStack(stack.current);
    } else if (navigationType === 'POP' && stack.current.length > 0) {
      stack.current = stack.current.slice(0, -1);
      persistStack(stack.current);
    }
  }

  const goBack = useCallback(() => {
    const target = stack.current[stack.current.length - 1];
    if (target === undefined) return;
    pendingBackTarget.current = target;
    navigate(target, { replace: true });
  }, [navigate]);

  return (
    <NavigationHistoryContext.Provider value={{ hasHistory: stack.current.length > 0, goBack }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
};
